import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { StyledButton } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import { StyledTypography } from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Link from "@waytek/MobiusOverrides/Link/Link";
import ProductDiscontinuedQuantityOrdered from "@waytek/Overrides/Components/ProductDiscontinuedQuantityOrdered";
import { completeProductUrl, isBulkProduct, productNotAvailable } from "@waytek/Utilities/global/products";
import { isAuthenticated, signInUrl } from "@waytek/Utilities/global/users";
import { fontFamily } from "@waytek/Utilities/variables";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    productSettings: getSettingsCollection(state).productSettings,
    isAuthenticated: isAuthenticated(state),
    signInUrl: signInUrl(state),
});

const mapDispatchToProps = {
    updateProductInfo,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    extendedStyles?: ProductCarouselProductFullStyles;
    actionsTemplate?: (product: ProductModel) => React.ReactNode;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductFullStyles {
    productWrapper?: InjectableCss;
    container?: GridContainerProps;
    productImageWrapper?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityOrderedStyles?: TextFieldProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    productInfoWrapper?: InjectableCss;
    productInfoTopWrapper?: InjectableCss;
    productInfoBottomWrapper?: InjectableCss;
    siteMessageWrapper?: InjectableCss;
    siteMessage?: TypographyPresentationProps;
    siteMessageLink?: InjectableCss;
    productPriceWrapper?: InjectableCss;
    productPrice?: ProductPriceStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    addToCartButtonGridItem?: GridItemProps;
}

const localColors = {
    globalIndustrialTaupe: getColor("common.global.industrialTaupe"),
    globalSecondaryGray: getColor("common.global.secondaryGray"),
    globalGuardianBlue: getColor("common.global.guardianBlue"),
    globalFadedTaupe: getColor("common.global.fadedTaupe"),
    globalWhite: getColor("common.global.white"),
};

export const productCarouselProductFullStyles: ProductCarouselProductFullStyles = {
    productWrapper: {
        css: css`
            border: solid 1px ${localColors.globalIndustrialTaupe};
            display: flex;
            flex-direction: column;
            height: 100%;
            background-color: ${localColors.globalFadedTaupe};
        `,
    },
    container: {
        gap: 8,
        css: css`
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
        `,
    },
    productImageWrapper: {
        css: css`
            border-bottom: solid 1px ${localColors.globalIndustrialTaupe};
            height: 210px;
            background-color: ${localColors.globalWhite};
            overflow: hidden;
        `,
    },
    productImageStyles: {
        img: {
            css: css`
                height: 100%;
                width: 100%;
                object-fit: contain;
            `,
        },

        linkWrappingImage: {
            css: css`
                justify-content: center;
                height: 100%;
                width: 100%;
            `,
        },
    },
    productInfoWrapper: {
        css: css`
            flex: auto;
            display: flex;
            flex-direction: column;
            padding: 20px 16px;
        `,
    },
    productInfoTopWrapper: {
        css: css`
            flex-grow: 0;
            margin: 0;
            margin-bottom: 20px;

            & > * {
                &:last-child {
                    margin-bottom: 0;
                    padding-bottom: 0;
                }

                &:first-child {
                    margin-top: 0;
                    padding-top: 0;
                }
            }
        `,
    },
    productInfoBottomWrapper: {
        css: css`
            flex-grow: 1;
            margin: 0;
            display: flex;
            flex-direction: column;

            & > * {
                &:last-child {
                    margin-bottom: 0;
                    padding-bottom: 0;
                }

                &:first-child {
                    margin-top: 0;
                    padding-top: 0;
                }
            }
        `,
    },
    siteMessageWrapper: {
        css: css`
            margin-top: 20px;
            margin-bottom: 20px;
        `,
    },
    siteMessage: {
        size: 14,
        weight: 600,
        lineHeight: 1.71,

        css: css`
            font-stretch: normal;
            font-style: normal;
            letter-spacing: normal;
        `,
    },
    siteMessageLink: {
        css: css`
            display: inline;

            ${StyledButton} {
                display: inline;
                border-bottom: 0;
            }

            ${TypographyStyle} {
                vertical-align: initial;
            }
        `,
    },
    productBrandWrapper: {
        css: css`
            margin: 0;
            margin-bottom: 4px;
            line-height: normal;
        `,
    },
    productBrandStyles: {
        nameLink: {
            css: css`
                width: auto;
            `,
        },
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                line-height: normal;

                &:hover {
                    ${StyledTypography} {
                        text-decoration: underline;
                    }
                }

                ${StyledButton} {
                    line-height: normal;
                    border: 0 !important;
                    text-decoration: none;
                }

                ${StyledTypography} {
                    font-family: ${fontFamily};
                    font-size: 16px;
                    font-weight: bold;
                    font-stretch: normal;
                    font-style: normal;
                    line-height: 1.5;
                    letter-spacing: normal;
                    border: 0 !important;
                    text-decoration: none;
                }
            `,
        },

        productDetailDescription: {
            css: css`
                display: none;
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 10px;
            `,
        },

        erpNumberGridItem: {
            css: css`
                &&& {
                    margin-top: 5px;
                }
            `,
        },

        erpNumberLabelText: {
            size: 14,
            weight: 600,
            lineHeight: 1.71,
        },

        erpNumberValueText: {
            size: 14,
            weight: "normal",
            lineHeight: 1.71,
        },

        manufacturerItemGridItem: {
            css: css`
                margin-top: 4px;
            `,
        },

        manufacturerItemLabelText: {
            size: 14,
            weight: 600,
            lineHeight: 1.71,
        },

        manufacturerItemValueText: {
            size: 14,
            weight: "normal",
            lineHeight: 1.71,
        },

        customerNameLabelText: {
            size: 14,
            weight: 600,
            lineHeight: 1.71,
        },

        customerNameValueText: {
            size: 14,
            weight: "normal",
            lineHeight: 1.71,
        },
    },
    productAvailabilityStyles: {
        messageText: {
            size: 14,
        },
        messageGridItem: {
            css: css`
                ${IconWrapper} {
                    width: 20px;
                    height: 20px;
                }
            `,
        },
        container: {
            css: css`
                margin-bottom: 10px;
            `,
        },
    },
    productUnitOfMeasureSelectStyles: {
        labelProps: {
            css: css`
                flex: 1;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                flex: 1;
            `,
        },
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
            `,
        },
    },
    productPriceWrapper: {
        css: css`
            margin: 0;
            margin-top: 16px;
            margin-bottom: 10px;
        `,
    },
    productPrice: {
        price: {
            priceText: {
                size: 21,
                weight: "normal",
                fontFamily,
                lineHeight: 1.19,
            },
            realTimeText: {
                size: 21,
                weight: "normal",
                fontFamily,
                lineHeight: 1.19,
            },
            unitOfMeasureText: {
                size: 18,
                weight: "normal",
                lineHeight: 1.61,
            },
        },
    },
    addToCartContainer: {
        gap: 10,
        css: css`
            margin: 0;
            margin-bottom: 11px;
            flex-grow: 0;
        `,
    },
    qtyGridItem: {
        width: 3,
        css: css`
            max-width: none;
            flex: 0 0 70px;
            padding-bottom: 0;
        `,
    },
    addToCartButtonGridItem: {
        width: 9,
        css: css`
            margin: 0;
            padding-bottom: 0;

            &&& {
                flex: auto;
            }
        `,
    },
    productAddToCartButtonStyles: {
        css: css`
            padding: 0;
            width: 100%;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 13 },
            css: css`
                margin: 0;
            `,
        },
    },
};

// eslint-disable-next-line
/* eslint-disable sonarjs/cognitive-complexity */
const ProductCarouselProductFull = ({
    carouselId,
    product,
    extendedStyles,
    productInfo,
    productSettings,
    updateProductInfo,
    actionsTemplate,
    isAuthenticated,
    signInUrl,
}: Props) => {
    // eslint-disable-next-line
    /* eslint-enable sonarjs/cognitive-complexity */
    const [styles] = useState(() => mergeToNew(productCarouselProductFullStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    let overrideShowBulkMessage = true;
    let overrideShowDiscontinuedMessage = true;
    let overrideShowPrice = true;
    let overrideShowAddToCart = true;
    let overrideShowAddToList = true;

    overrideShowBulkMessage = isAuthenticated ? false : isBulkProduct(product.productNumber);
    overrideShowDiscontinuedMessage = product.isDiscontinued;
    if (isBulkProduct(product.productNumber)) {
        overrideShowPrice = isAuthenticated;
    } else {
        overrideShowPrice = !productNotAvailable(product, productInfo);
    }
    if (isBulkProduct(product.productNumber)) {
        overrideShowAddToCart = isAuthenticated;
    } else {
        overrideShowAddToCart = !productNotAvailable(product, productInfo);
    }
    if (isBulkProduct(product.productNumber)) {
        overrideShowAddToList = isAuthenticated;
    } else {
        overrideShowAddToList = !productNotAvailable(product, productInfo);
    }

    return (
        <StyledWrapper {...styles.productWrapper}>
            <ProductContext.Provider value={productContext}>
                <StyledWrapper
                    id={`productContainer_${product.id}`}
                    {...styles.container}
                    data-test-selector="productContainer"
                >
                    <StyledWrapper {...styles.productImageWrapper}>
                        <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                    </StyledWrapper>

                    <StyledWrapper {...styles.productInfoWrapper}>
                        <StyledWrapper {...styles.productInfoTopWrapper}>
                            {product.brand && (
                                <StyledWrapper {...styles.productBrandWrapper}>
                                    <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                                </StyledWrapper>
                            )}

                            <ProductDescription
                                product={productContext}
                                extendedStyles={styles.productDescriptionStyles}
                            />

                            <ProductPartNumbers
                                productNumber={product.productNumber}
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={product.manufacturerItem}
                                showCustomerName={true}
                                showManufacturerItem={true}
                                showLabel={true}
                                extendedStyles={styles.productPartNumbersStyles}
                            />

                            <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                        </StyledWrapper>

                        <StyledWrapper {...styles.productInfoBottomWrapper}>
                            {overrideShowBulkMessage && (
                                <StyledWrapper {...styles.siteMessageWrapper}>
                                    <Typography {...styles.siteMessage}>
                                        {siteMessage("CSM_You_Must_Be_Logged_In")}{" "}
                                        <Link
                                            data-test-selector="product_signIn"
                                            href={signInUrl}
                                            {...styles.siteMessageLink}
                                        >
                                            {siteMessage("CSM_Log_in_here")}
                                        </Link>
                                    </Typography>
                                </StyledWrapper>
                            )}

                            {overrideShowDiscontinuedMessage && (
                                <StyledWrapper {...styles.siteMessageWrapper}>
                                    {product.detail?.replacementProductId ? (
                                        <Typography {...styles.siteMessage}>
                                            {siteMessage("CSM_Item_Discontinued_Replaced")}{" "}
                                            <Link
                                                data-test-selector="product_signIn"
                                                href={completeProductUrl(product.properties?.replacementProductUrl)}
                                                {...styles.siteMessageLink}
                                                style={{ fontWeight: "bold" }}
                                            >
                                                {product.properties?.replacementProductNumber}
                                            </Link>
                                        </Typography>
                                    ) : (
                                        <Typography {...styles.siteMessage}>
                                            {siteMessage("CSM_Item_Discontinued")}
                                        </Typography>
                                    )}
                                </StyledWrapper>
                            )}

                            <div style={{ marginTop: "auto" }} />

                            {overrideShowPrice && (
                                <StyledWrapper {...styles.productPriceWrapper}>
                                    <VisuallyHidden
                                        as="label"
                                        htmlFor={`${carouselId}-uom`}
                                        id={`${carouselId}-uom-label`}
                                    >
                                        {translate("U/M")}
                                    </VisuallyHidden>
                                    <ProductPrice
                                        showLabel={false}
                                        product={productContext}
                                        extendedStyles={styles.productPrice}
                                    />
                                    {product.unitOfMeasures && (
                                        <ProductUnitOfMeasureSelect
                                            labelOverride=""
                                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                            id={`${carouselId}-uom`}
                                        />
                                    )}
                                </StyledWrapper>
                            )}

                            {overrideShowAddToCart && (
                                <GridContainer {...styles.addToCartContainer} gap={8}>
                                    <GridItem {...styles.qtyGridItem}>
                                        <VisuallyHidden
                                            as="label"
                                            htmlFor={`${carouselId}-qty`}
                                            id={`${carouselId}-qty-label`}
                                        >
                                            {translate("Qty")}
                                        </VisuallyHidden>
                                        {product.isDiscontinued ? (
                                            <ProductDiscontinuedQuantityOrdered
                                                labelOverride=""
                                                extendedStyles={styles.productQuantityOrderedStyles}
                                                id={`${carouselId}-qty`}
                                            />
                                        ) : (
                                            <ProductQuantityOrdered
                                                labelOverride=""
                                                extendedStyles={styles.productQuantityOrderedStyles}
                                                id={`${carouselId}-qty`}
                                            />
                                        )}
                                    </GridItem>
                                    <GridItem {...styles.addToCartButtonGridItem}>
                                        <ProductAddToCartButton
                                            data-test-selector="addToCartBtn"
                                            extendedStyles={styles.productAddToCartButtonStyles}
                                        />
                                    </GridItem>
                                </GridContainer>
                            )}

                            {overrideShowAddToList && (
                                <ProductAddToListLink
                                    data-test-selector="addToListLink"
                                    extendedStyles={styles.productAddToListLinkStyles}
                                />
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>

                {actionsTemplate ? actionsTemplate(product) : []}
            </ProductContext.Provider>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProductFull);
